import { buildingPackageSchema, createBuildingPackageResponseSchema, updateBuildingPackageResponseSchema, buildingPackagesSchema, getBuildingPackageFilesResponseSchema, getBuildingPackageResultsResponseSchema, buildingPackageResultSchema, createBuildingJSONResponseSchema, updateBuildingJSONResponseSchema, } from '@consigli/types';
import { createTransformer, createParams, mapToSnakeCase, } from './shared/base-api';
export const buildingPackageApiEndpoints = (builder, coreApiBaseUrl) => ({
    getBuildingPackage: builder.query({
        query: ({ projectId, buildingPackageId }) => `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/`,
        providesTags: ['BuildingPackages'],
        transformResponse: createTransformer(buildingPackageSchema, 'GetBuildingPackage'),
    }),
    getBuildingPackageFiles: builder.query({
        query: ({ projectId, buildingPackageId, page, pageSize, search }) => {
            const params = createParams({ page, pageSize, search });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/files/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['BuildingPackageFiles'],
        transformResponse: createTransformer(getBuildingPackageFilesResponseSchema, 'getBuildingPackageFiles'),
    }),
    getBuildingPackages: builder.query({
        query: ({ projectId, type, page, pageSize }) => {
            const params = createParams({ type, page, pageSize });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['BuildingPackages'],
        transformResponse: createTransformer(buildingPackagesSchema, 'getBuildingPackages'),
    }),
    createBuildingPackage: builder.mutation({
        query: ({ projectId, payload }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/`,
            method: 'POST',
            body: mapToSnakeCase(payload),
        }),
        invalidatesTags: ['Projects', 'BuildingPackages'],
        transformResponse: createTransformer(createBuildingPackageResponseSchema, 'CreateBuildingPackage'),
    }),
    updateBuildingPackage: builder.mutation({
        query: ({ projectId, buildingPackageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/`,
            method: 'PATCH',
            body: mapToSnakeCase(data),
        }),
        invalidatesTags: ['BuildingPackages'],
        transformResponse: createTransformer(updateBuildingPackageResponseSchema, 'UpdateBuildingPackage'),
    }),
    getBuildingPackageResults: builder.query({
        query: ({ projectId, buildingPackageId, page, pageSize, search }) => {
            const params = createParams({ page, pageSize, search });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/results/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['BuildingPackageResults'],
        transformResponse: createTransformer(getBuildingPackageResultsResponseSchema, 'getBuildingPackageResults'),
    }),
    getBuildingPackageBuildingJson: builder.query({
        query: ({ projectId, buildingPackageId }) => `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/building-json/`,
        providesTags: ['BuildingPackages'],
    }),
    createBuildingPackageBuildingJson: builder.mutation({
        query: ({ projectId, buildingPackageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/building-json/`,
            method: 'POST',
            body: mapToSnakeCase({ data }),
        }),
        invalidatesTags: ['BuildingPackages'],
        transformResponse: createTransformer(createBuildingJSONResponseSchema, 'createBuildingPackageBuildingJson'),
    }),
    updateBuildingPackageBuildingJson: builder.mutation({
        query: ({ projectId, buildingPackageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/building-json/`,
            method: 'PATCH',
            body: mapToSnakeCase({ data }),
        }),
        invalidatesTags: ['BuildingPackages'],
        transformResponse: createTransformer(updateBuildingJSONResponseSchema, 'updateBuildingPackageBuildingJson'),
    }),
    createBuildingPackageResult: builder.mutation({
        query: ({ projectId, buildingPackageId, buildingPackageResult }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/results/`,
            method: 'POST',
            body: mapToSnakeCase(buildingPackageResult),
        }),
        invalidatesTags: ['BuildingPackageResults'],
        transformResponse: createTransformer(buildingPackageResultSchema, 'createBuildingPackageResult'),
    }),
    getBuildingPackageResult: builder.query({
        query: ({ projectId, buildingPackageId, resultId }) => `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/results/${resultId}/`,
        providesTags: ['BuildingPackageResults'],
        transformResponse: createTransformer(buildingPackageResultSchema, 'getBuildingPackageResult'),
    }),
    updateBuildingPackageResult: builder.mutation({
        query: ({ projectId, buildingPackageId, resultId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/results/${resultId}/`,
            method: 'PATCH',
            body: mapToSnakeCase(data),
        }),
        invalidatesTags: ['BuildingPackageResults'],
        transformResponse: createTransformer(buildingPackageResultSchema, 'updateBuildingPackageResult'),
    }),
    deleteBuildingPackageResult: builder.mutation({
        query: ({ projectId, buildingPackageId, resultId }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/results/${resultId}/`,
            method: 'DELETE',
        }),
        invalidatesTags: ['BuildingPackageResults'],
    }),
    deleteBuildingPackageFile: builder.mutation({
        query: ({ projectId, buildingPackageId, fileId }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/files/${fileId}/`,
            method: 'DELETE',
        }),
        invalidatesTags: ['BuildingPackageFiles'],
    }),
    addBuildingPackageFiles: builder.mutation({
        query: ({ projectId, buildingPackageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/`,
            method: 'PATCH',
            body: data,
        }),
        invalidatesTags: ['BuildingPackageFiles', 'BuildingPackages'],
        transformResponse: createTransformer(buildingPackageSchema, 'addBuildingPackageFiles'),
    }),
    notifyPublishedBuildingPackage: builder.mutation({
        query: ({ projectId, buildingPackageId, notificationType, language, emailRecipient, message, }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/building-packages/${buildingPackageId}/notify/`,
            method: 'POST',
            body: {
                notification_type: notificationType,
                language,
                email_recipient: emailRecipient,
                message,
            },
        }),
        invalidatesTags: ['BuildingPackages'],
    }),
});
